import React from 'react';

const InterestBreakdown = ({ breakdown, bankName, websiteUrl }) => {
  console.log("InterestBreakdown received breakdown:", breakdown);
  
  if (!breakdown || breakdown.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-gray-500">No breakdown available.</p>
      </div>
    );
  }

  // Check if we're using the old format (with isHeader/isTotal properties)
  const isOldFormat = breakdown.some(item => item.isHeader || item.isTotal);
  console.log("Using old format:", isOldFormat);

  if (isOldFormat) {
    // Look for the Total Annual Interest item
    const totalItem = breakdown.find(item => 
      item.description && item.description.includes("Total Annual Interest"));
    
    console.log("Found total item:", totalItem);
    
    return (
      <div className="bg-white/50 rounded-lg p-3">
        {/* Bank header with name and website link - hidden on mobile, visible on md+ screens */}
        <div className="flex items-center mb-4">
          <span className="hidden md:flex font-medium text-gray-700">{bankName || 'Bank'}</span>
          {websiteUrl && (
            <a 
              href={websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:flex ml-2 text-xs text-purple-700 hover:text-purple-800 hover:underline items-center cursor-pointer z-10 relative px-1 py-0.5"
            >
              Website
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          )}
        </div>

        {breakdown.map((item, index) => {
          if (item.isHeader) {
            return (
              <p key={index} className="font-medium text-gray-700 mb-2">
                {item.description}
              </p>
            );
          }
          
          if (item.isTotal) {
            return (
              <div key={index} className="flex justify-between border-t border-gray-200 pt-2 mt-2">
                <span className="font-medium">{item.description}</span>
                <span className="font-medium">${item.tierInterest !== undefined ? item.tierInterest.toFixed(2) : '0.00'}</span>
              </div>
            );
          }

          return (
            <div key={index} className="flex justify-between text-sm">
              <span>{item.description}</span>
              <span>${item.tierInterest !== undefined ? item.tierInterest.toFixed(2) : '0.00'}</span>
            </div>
          );
        })}
      </div>
    );
  }

  // Calculate total interest for the new format
  const calculateTotal = () => {
    console.log("Calculating total for new format");
    let total = 0;
    
    try {
      breakdown.forEach((period, periodIndex) => {
        console.log(`Period ${periodIndex}:`, period);
        
        if (period.tiers && Array.isArray(period.tiers)) {
          period.tiers.forEach((tier, tierIndex) => {
            console.log(`  Tier ${tierIndex}:`, tier);
            
            if (tier.interest !== undefined) {
              console.log(`    Adding tier.interest: ${tier.interest}`);
              total += Number(tier.interest);
            } else if (tier.tierInterest !== undefined) {
              console.log(`    Adding tier.tierInterest: ${tier.tierInterest}`);
              total += Number(tier.tierInterest);
            } else if (tier.amount !== undefined && tier.rate !== undefined) {
              const interest = tier.amount * (tier.rate / 100);
              console.log(`    Calculating from amount(${tier.amount}) and rate(${tier.rate}): ${interest}`);
              total += interest;
            } else if (tier.amountInTier !== undefined && tier.tierRate !== undefined) {
              const interest = tier.amountInTier * tier.tierRate;
              console.log(`    Calculating from amountInTier(${tier.amountInTier}) and tierRate(${tier.tierRate}): ${interest}`);
              total += interest;
            }
          });
        } else if (period.tierInterest !== undefined) {
          // Handle case where period itself has a tierInterest property
          console.log(`  Adding period.tierInterest: ${period.tierInterest}`);
          total += Number(period.tierInterest);
        }
      });
    } catch (error) {
      console.error("Error calculating total:", error);
    }
    
    console.log("Calculated total:", total);
    return total;
  };
  
  const totalAnnualInterest = calculateTotal();

  // New format (with period.title and period.tiers)
  return (
    <div className="bg-white/50 rounded-lg p-3">
      {/* Bank header with name and website link - hidden on mobile, visible on md+ screens */}
      <div className="flex items-center mb-4">
        <span className="hidden md:flex font-medium text-gray-700">{bankName || 'Bank'}</span>
        {websiteUrl && (
          <a 
            href={websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:flex ml-2 text-xs text-purple-700 hover:text-purple-800 hover:underline items-center cursor-pointer z-10 relative px-1 py-0.5"
            onClick={(e) => {
              e.stopPropagation();
              window.open(websiteUrl, '_blank', 'noopener,noreferrer');
            }}
          >
            Website
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        )}
      </div>

      {breakdown.map((period, index) => (
        <div key={index}>
          <p key={`title-${index}`} className="font-medium text-gray-700 mb-2">
            {period.title}
          </p>
          
          {period.tiers && period.tiers.map((tier, tierIndex) => (
            <div key={`tier-${index}-${tierIndex}`} className="flex justify-between border-t border-gray-200 pt-2 mt-2">
              <span>{tier.description}</span>
              <span className="font-medium">
                {tier.rate !== undefined ? `${tier.rate}%` : 
                tier.tierRate !== undefined ? `${(tier.tierRate * 100).toFixed(2)}%` : '0.00%'}
              </span>
            </div>
          ))}
        </div>
      ))}
      
      {/* Add total annual interest */}
      <div className="flex justify-between border-t border-gray-200 pt-2 mt-4">
        <span className="font-medium">Total Annual Interest:</span>
        <span className="font-medium">${totalAnnualInterest.toFixed(2)}</span>
      </div>
    </div>
  );
};

export default InterestBreakdown; 